import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import VendorModal from '../../../components/modals/VendorModal';
import ExportModal from '../../../components/modals/ExportModal';
import { VendorContext } from '../../../context/VendorProvider';
import { WebsiteContext } from '../../../context/WebsiteProvider';
import { UserContext } from '../../../context/UserProvider';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import exportIcon from '../../../assets/icons/export.svg';
import recipAllowed from '../../../assets/icons/recipAllowed.png';
import recip from '../../../assets/icons/recip.png';
import provider from '../../../assets/icons/provider.png';
import consumer from '../../../assets/icons/consumer.png';
import { ReactComponent as FilterIcon } from '../../../assets/icons/filter.svg';
import { MdFilterAltOff } from 'react-icons/md';
import { FaLink, FaSpinner } from 'react-icons/fa';
import SuccessNotification from '../../../components/toast/SuccessNotification';
import ErrorNotification from '../../../components/toast/ErrorNotification';
import TetrisModal from '../../../components/modals/TetrisModal';
import {LuImport} from "react-icons/lu";

const useSortableData = (items, config = null) => {
  const [sortConfig, setSortConfig] = useState(config);

  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig !== null && sortConfig.direction !== null) {
      sortableItems.sort((a, b) => {
        if (sortConfig.key === 'activeSites') {
          const numA = parseFloat(a[sortConfig.key]) || 0;
          const numB = parseFloat(b[sortConfig.key]) || 0;
          return sortConfig.direction === 'ascending'
            ? numA - numB
            : numB - numA;
        }
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';

        const valAAlpha = valA.replace(/[^a-zA-Z]/g, '');
        const valBAlpha = valB.replace(/[^a-zA-Z]/g, '');

        const valANumSpec = valA.replace(/[a-zA-Z]/g, '');
        const valBNumSpec = valB.replace(/[a-zA-Z]/g, '');

        if (valAAlpha < valBAlpha)
          return sortConfig.direction === 'ascending' ? -1 : 1;
        if (valAAlpha > valBAlpha)
          return sortConfig.direction === 'ascending' ? 1 : -1;

        if (valANumSpec < valBNumSpec)
          return sortConfig.direction === 'ascending' ? -1 : 1;
        if (valANumSpec > valBNumSpec)
          return sortConfig.direction === 'ascending' ? 1 : -1;

        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') {
        direction = 'descending';
      } else if (sortConfig.direction === 'descending') {
        direction = null;
      }
    }
    setSortConfig({ key, direction });
  };

  const getSortDirection = (key) => {
    if (sortConfig && sortConfig.key === key) {
      return sortConfig.direction;
    }
    return undefined;
  };

  return { items: sortedItems, requestSort, getSortDirection };
};

const VendorTable = ({ onSwitchToImports }) => {
  const {
    showSuccess,
    setShowSuccess,
    showError,
    setShowError,
    text,
    openModal,
    closeVendorModal,
    isVendorOpen,
    data,
    handleVendorClick,
    selectedVendor,
    setSelectedVendor,
    setVendorName,
    getTableFeedTypes,
    getTableVendorTypes,
    tableFeedTypes,
    tableVendorTypes,
    getAllVendors,
  } = useContext(VendorContext);
  const {
    hideInActive,
    setHideInActive,
    setUserPreference,
    darkMode,
    isExpanded,
    token,
    role,
    vendorColumnVisibility,
    setVendorColumnVisibility,
  } = useContext(UserContext);
  const { getAllWebsites } = useContext(WebsiteContext);
  const [showTetrisModal, setShowTetrisModal] = useState(false);
  const [showRickRoll, setShowRickRoll] = useState(false);
  const [clickedOutsideLegend, setClickedOutsideLegend] = useState(false);
  const [doubleClickRequired, setDoubleClickRequired] = useState(false);
  const [shouldCloseDropdown, setShouldCloseDropdown] = useState(true);
  const [ignoreNextRowClick, setIgnoreNextRowClick] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [openDropdownField, setOpenDropdownField] = useState(null);
  const doubleClickTimeout = useRef(null);
  const legendRef = useRef(null);
  const { items, requestSort, getSortDirection } = useSortableData(data);
  const [localVendorTypes, setLocalVendorTypes] = useState([]);
  const [localFeedTypes, setLocalFeedTypes] = useState([]);
  const [checkboxStatus, setCheckboxStatus] = useState({});
  const [maxHeight, setMaxHeight] = useState('85vh');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportOptions, setExportOptions] = useState({
    vendor: true,
    vendorCode: true,
    status: true,
    feedType: true,
    vendorType: true,
    totalSites: true,
    activeSites: true,
    hasReciprocal: true,
    startDate: true,
    endDate: true,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  const iconConfigurations = {
    hasReciprocal: {
      icon: (
        <img
          src={recip}
          alt="Has Reciprocal Entries"
          className="mr-2 inline h-4 w-4 recip"
        />
      ),
      label: "Has Reciprocal Entries",
    },
    reciprocalAllowed: {
      icon: (
        <img
          src={recipAllowed}
          alt="Reciprocal Allowed"
          className="mr-2 inline h-4 w-4 recipAllowed"
        />
      ),
      label: 'Reciprocal Allowed',
    },
    isProvider: {
      icon: (
        <img
          src={provider}
          alt="Is Provider"
          className="mr-2 inline h-4 w-4 provider"
        />
      ),
      label: 'Is Provider',
    },
    isConsumer: {
      icon: (
        <img
          src={consumer}
          alt="Is Consumer"
          className="mr-2 inline h-4 w-4 consumer"
        />
      ),
      label: 'Is Consumer',
    },
    masterLinkedVendor: {
      icon: (
        <span className="master-link-icon">
          <FaLink style={{ color: 'gold', marginRight: '10px' }} />
        </span>
      ),
      label: 'Master Linked Vendor',
    },
    linkedVendor: {
      icon: (
        <span className="linked-icon">
          <FaLink style={{ color: 'gray', marginRight: '10px' }} />
        </span>
      ),
      label: 'Linked Vendor',
    },
  };

  const [iconFilters, setIconFilters] = useState({
    documentsWaiting: false,
    hasReciprocal: false,
    reciprocalAllowed: false,
    isProvider: false,
    isConsumer: false,
    masterLinkedVendor: false,
    linkedVendor: false,
  });

  const toggleFilter = (filterKey) => {
    let newFilter = { ...iconFilters };
    Object.keys(iconFilters).forEach((key) => {
      newFilter[key] = key === filterKey ? !iconFilters[key] : iconFilters[key];
    });
    setIconFilters(newFilter);
  };

  const handleInActive = async () => {
    const newHideInActive = !hideInActive;
    await setUserPreference({
      newDarkMode: darkMode,
      newIsExpanded: isExpanded,
      newHideInActive: newHideInActive,
    });
    setHideInActive(newHideInActive);
  };

  useEffect(() => {
    getAllWebsites();
  }, [token]);

  useEffect(() => {
    const updateMaxHeight = () => {
      const vhToPixels = (vh) => window.innerHeight * (vh / 99);
      let calculatedHeight = vhToPixels(80.6);
      const isChrome =
        /Chrome/.test(navigator.userAgent) &&
        /Google Inc/.test(navigator.vendor);
      if (isChrome) {
        calculatedHeight += 18;
      }
      setMaxHeight(`${calculatedHeight}px`);
    };
    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, []);

  const handleDropdownClick = (e, field) => {
    e.stopPropagation();
    setIgnoreNextRowClick(true);

    if (field === 'feedTypeString') {
      getTableFeedTypes();
    } else if (field === 'vendorTypeString') {
      getTableVendorTypes();
    }

    if (openDropdownField === field) {
      setOpenDropdownField(null);
    } else {
      setOpenDropdownField(field);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!tableFeedTypes.length) await getTableFeedTypes();
      if (!tableVendorTypes.length) await getTableVendorTypes();
      await getAllVendors().then(() => setIsInitialLoad(false));
    };
    fetchData();
  }, []);

  useEffect(() => {
    setLocalFeedTypes(tableFeedTypes);
    setLocalVendorTypes(tableVendorTypes);
  }, []);

  useEffect(() => {
    if (!isVendorOpen) {
      setLocalFeedTypes(tableFeedTypes);
      setLocalVendorTypes(tableVendorTypes);
    }
  }, [isVendorOpen]);

  useEffect(() => {
    if (openDropdownField === null && doubleClickTimeout.current === null) {
      setDoubleClickRequired(true);
      doubleClickTimeout.current = setTimeout(() => {
        setDoubleClickRequired(false);
        doubleClickTimeout.current = null;
      }, 100);
    }
  }, [openDropdownField]);

  useEffect(() => {
    if (openDropdownField === null) {
      triggerDoubleClickRequirement();
    }
  }, [openDropdownField]);

  const triggerDoubleClickRequirement = () => {
    if (doubleClickTimeout.current === null) {
      doubleClickTimeout.current = setTimeout(() => {
        doubleClickTimeout.current = null;
      }, 100);
    }
  };

  const toggleLegend = () => {
    setShowLegend(!showLegend);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        legendRef.current &&
        !legendRef.current.contains(event.target) &&
        showLegend
      ) {
        setShowLegend(false);
        setClickedOutsideLegend(true);
        setTimeout(() => setClickedOutsideLegend(false), 100);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLegend]);

  const handleRowClick = (id, e) => {
    e.stopPropagation();
    if (ignoreNextRowClick) {
      setIgnoreNextRowClick(false);
      return;
    }
    if (doubleClickRequired) {
      if (e.detail === 2) {
        handleVendorClick(id);
      }
    } else {
      if (!clickedOutsideLegend) {
        handleVendorClick(id);
      }
    }
  };

  const handleNewVendorClick = () => {
    setSelectedVendor(null);
    setVendorName('New Vendor');
    openModal();
  };

  const filteredItems = useMemo(() => {
    if (!items) return [];
    return items.filter((item) => {
      if (hideInActive && item.Status === 'Inactive') return false;

      if (
        searchTerm &&
        !Object.values(item).some((value) =>
          typeof value === 'string' ? value.toLowerCase().includes(searchTerm.toLowerCase()) : false
        )
      ) return false;

      const iconFilterApplied = Object.keys(iconFilters).some((key) => iconFilters[key]);
      const iconMatch = iconFilterApplied
        ? Object.entries(iconFilters).some(([key, value]) => {
          if (!value) return false;
          if (key === 'hasReciprocal') return item.hasReciprocal === true;
          if (key === 'masterLinkedVendor') {
            return item.linkedGroup && item.linkedGroup.length >= 2 && item.isPrimary === true;
          }
          if (key === 'linkedVendor') {
            return item.linkedGroup && item.linkedGroup.length >= 2 && item.isPrimary === false;
          }
          return item[key] === true;
        })
        : true;

      const checkboxMatch = Object.keys(checkboxStatus).every((key) => {
        if (!checkboxStatus[key] || checkboxStatus[key].length === 0) return true;
        if (key === 'feedTypeString') {
          const feedType = localFeedTypes.find((type) => type.type === item[key]);
          return feedType && checkboxStatus[key].includes(feedType.id);
        }
        if (key === 'vendorTypeString') {
          const vendorType = localVendorTypes.find((type) => type.type === item[key]);
          return vendorType && checkboxStatus[key].includes(vendorType.id);
        }
        return typeof item[key] === 'string' && checkboxStatus[key].includes(item[key]);
      });

      return (iconFilterApplied ? iconMatch : true) && checkboxMatch;
    });
  }, [items, searchTerm, checkboxStatus, localFeedTypes, localVendorTypes, hideInActive, iconFilters]);

  const exportKeyLabels = {
    vendor: 'Vendor',
    vendorCode: 'Vendor Code',
    status: 'Status',
    feedType: 'Feed Type',
    vendorType: 'Vendor Type',
    activeSites: 'Active Sites',
    totalSites: 'Total Sites',
    inactiveSites: 'Inactive Sites',
    hasReciprocal: 'Has Reciprocal',
    startDate: 'Start Date',
    endDate: 'End Date',
  };

  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));

    let filteredData = filteredItems;
    if (startDate || endDate) {
      const startDateTime = startDate
        ? new Date(startDate).getTime()
        : -Infinity;
      const endDateTime = endDate ? new Date(endDate).getTime() : Infinity;

      filteredData = filteredData.filter((item) => {
        if (!item.startDate) return false;
        const itemDateTime = new Date(item.startDate).getTime();
        return itemDateTime >= startDateTime && itemDateTime <= endDateTime;
      });
    }

    const exportKeys = {
      vendor: 'Vendor',
      vendorCode: 'Vendor Code',
      status: 'Status',
      feedType: 'Feed Type',
      vendorType: 'Vendor Type',
      activeSites: 'Active Sites',
      totalSites: 'Total Sites',
      inactiveSites: 'Inactive Sites',
      hasReciprocal: 'Has Reciprocal',
      startDate: 'Start Date',
      endDate: 'End Date',
    };

    try {
      const formattedData = filteredData.map((item) => {
        let exportItem = {};
        for (const [optionKey, label] of Object.entries(exportKeys)) {
          if (exportOptions[optionKey]) {
            switch (optionKey) {
              case 'vendor':
                exportItem[label] = item.Vendor;
                break;
              case 'vendorCode':
                exportItem[label] = item['Vendor Code'];
                break;
              case 'status':
                exportItem[label] = item.Status;
                break;
              case 'feedType':
                exportItem[label] = item.feedTypeString;
                break;
              case 'vendorType':
                exportItem[label] = item.vendorTypeString;
                break;
              case 'activeSites':
                exportItem[label] = item.activeSites;
                break;
              case 'totalSites':
                exportItem[label] = item.totalSites;
                break;
              case 'inactiveSites':
                exportItem[label] = item.inactiveSites;
                break;
              case 'hasReciprocal':
                exportItem[label] = item.hasReciprocal;
                break;
              case 'startDate':
                exportItem[label] = item.startDate;
                break;
              case 'endDate':
                exportItem[label] = item.endDate;
                break;
              default:
                break;
            }
          }
        }
        return exportItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Export');
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      saveAs(blob, 'VendorTable.xlsx');
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      alert("An error occurred during export. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const summaryData = useMemo(() => {
    const totalInactive = filteredItems.filter(
      (item) => item.Status === 'Inactive'
    ).length;
    const totalActive = filteredItems.filter(
      (item) => item.Status === 'Active'
    ).length;
    const totalPending = filteredItems.filter(
      (item) => item.Status === 'Pending Activation'
    ).length;

    return {
      total: filteredItems.length.toLocaleString(),
      active: totalActive.toLocaleString(),
      inactive: totalInactive.toLocaleString(),
      pending: totalPending.toLocaleString(),
    };
  }, [filteredItems]);

  const defaultColumns = [
    { header: 'Vendor', field: 'Vendor', width: 230 },
    { header: 'Vendor Code', field: 'Vendor Code', width: 150 },
    { header: 'Status', field: 'Status', width: 180, filter: true },
    { header: 'Feed Type', field: 'feedTypeString', width: 200, filter: true },
    { header: 'Vendor Type', field: 'vendorTypeString', width: 200, filter: true },
    { header: 'Sites Active', field: 'activeSites', width: 100 },
  ];

  const defaultColumnVisibility = [
    { columnName: 'Vendor', visibility: true, width: 230 },
    { columnName: 'Vendor Code', visibility: true, width: 150 },
    { columnName: 'Status', visibility: true, width: 180 },
    { columnName: 'feedTypeString', visibility: true, width: 200 },
    { columnName: 'vendorTypeString', visibility: true, width: 200 },
    { columnName: 'activeSites', visibility: true, width: 100 },
  ];

  const storedVendorColumnVisibility = localStorage.getItem('vendorColumnVisibility');
  const initialColumnVisibility = storedVendorColumnVisibility
    ? JSON.parse(storedVendorColumnVisibility)
    : vendorColumnVisibility.length > 0
      ? vendorColumnVisibility
      : defaultColumnVisibility;

  const [columnVisibility, setColumnVisibility] = useState(initialColumnVisibility);

  const [columns, setColumns] = useState(() => {
    const cols = initialColumnVisibility.map((col) => {
      const defaultCol = defaultColumns.find((dc) => dc.field === col.columnName) || {};
      return {
        ...defaultCol,
        header: col.columnName === 'Status' ? 'Status' : col.columnName.charAt(0).toUpperCase() + col.columnName.slice(1),
        field: col.columnName,
        width: col.width || defaultCol.width || 160,
        ...(col.columnName === 'Status' && { filter: true, filterOptions: ['Active', !hideInActive && 'Inactive'].filter(Boolean) }),
        ...(col.columnName === 'feedTypeString' && { filter: true, filterOptions: tableFeedTypes.map((ft) => ft.type) }),
        ...(col.columnName === 'vendorTypeString' && { filter: true, filterOptions: tableVendorTypes.map((vt) => vt.type) }),
      };
    });
    return cols;
  });

  useEffect(() => {
    const updatedColumns = columnVisibility.map((col) => {
      const defaultCol = defaultColumns.find((dc) => dc.field === col.columnName) || {};
      let header;
      switch (col.columnName) {
        case 'feedTypeString':
          header = 'Feed Type';
          break;
        case 'vendorTypeString':
          header = 'Vendor Type';
          break;
        case 'Status':
          header = 'Status';
          break;
        default:
          header = col.columnName.charAt(0).toUpperCase() + col.columnName.slice(1);
      }
      return {
        ...defaultCol,
        header,
        field: col.columnName,
        width: col.width || defaultCol.width || 160,
        ...(col.columnName === 'Status' && { filter: true, filterOptions: ['Active', !hideInActive && 'Inactive'].filter(Boolean) }),
        ...(col.columnName === 'feedTypeString' && { filter: true, filterOptions: tableFeedTypes.map((ft) => ft.type) }),
        ...(col.columnName === 'vendorTypeString' && { filter: true, filterOptions: tableVendorTypes.map((vt) => vt.type) }),
      };
    });
    setColumns(updatedColumns);
  }, [columnVisibility, hideInActive, tableFeedTypes, tableVendorTypes]);

  useEffect(() => {
    const fetchPreferences = async () => {
      const updatedVisibility = vendorColumnVisibility.length > 0 ? vendorColumnVisibility : defaultColumnVisibility;
      setColumnVisibility(updatedVisibility);
      setVendorColumnVisibility(updatedVisibility);
    };
    fetchPreferences();
  }, [setVendorColumnVisibility]);

  useEffect(() => {
    if (vendorColumnVisibility.length > 0) {
      setColumnVisibility(vendorColumnVisibility);
    }
  }, [vendorColumnVisibility]);

  const visibleColumns = columns.filter(
    (column) =>
      columnVisibility.find((col) => col.columnName === column.field)?.visibility
  );

  const handleColumnToggle = (field) => {
    const updatedVisibility = columnVisibility.map((col) =>
      col.columnName === field ? { ...col, visibility: !col.visibility } : col
    );
    setColumnVisibility(updatedVisibility);
    setVendorColumnVisibility(updatedVisibility);
    setUserPreference({ newVendorColumnVisibility: updatedVisibility });
  };

  const handleResize = (field, newWidth) => {
    const updatedColumns = columns.map((col) =>
      col.field === field ? { ...col, width: newWidth } : col
    );
    setColumns(updatedColumns);

    const updatedVisibility = columnVisibility.map((col) =>
      col.columnName === field ? { ...col, width: newWidth } : col
    );
    setColumnVisibility(updatedVisibility);
    setVendorColumnVisibility(updatedVisibility);

    return updatedVisibility;
  };

  const commitResize = (updatedVisibility) => {
    setUserPreference({ newVendorColumnVisibility: updatedVisibility });
  };

  const ColumnsDropdown = () => {
    const isOpen = openDropdownField === 'columns';

    if (!isOpen) return null;

    return (
      <div
        ref={dropdownRef}
        className="absolute left-24 z-50 rounded border border-gray-200 bg-white p-3 shadow-lg dark:border-stone-600 dark:bg-stone-700"
        style={{
          top: 'calc(100% + 0.5rem)',
          minWidth: '15rem',
          textAlign: 'left',
        }}
      >
        <div className="flex w-full flex-col space-y-1">
          {columns.map((column, i) => (
            <label
              key={i}
              className="flex items-center space-x-2 p-1"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                className="form-checkbox rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                checked={
                  columnVisibility.find((col) => col.columnName === column.field)?.visibility
                }
                onChange={() => handleColumnToggle(column.field)}
              />
              <span className="w-full text-sm text-gray-900 dark:text-gray-300">
                {column.header}
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const FilterDropdown = ({ field, isOpen }) => {
    if (field !== openDropdownField) return null;

    let options = [];
    if (field === 'Status') {
      options = [{ label: 'Active', value: 'Active' }];
      if (!hideInActive) {
        options.push({ label: 'Inactive', value: 'Inactive' });
      }
    } else if (field === 'feedTypeString') {
      options = tableFeedTypes.map((feedType) => ({
        label: feedType.type,
        value: feedType.id,
      }));
    } else if (field === 'vendorTypeString') {
      options = tableVendorTypes.map((vendorType) => ({
        label: vendorType.type,
        value: vendorType.id,
      }));
    }

    const headerName = field;

    const handleFilterOptionClick = (event, headerName, optionValue) => {
      setCheckboxStatus((prevStatus) => {
        const updatedStatus = { ...prevStatus };
        const currentValues = updatedStatus[headerName] || [];
        if (currentValues.includes(optionValue)) {
          updatedStatus[headerName] = currentValues.filter(
            (value) => value !== optionValue
          );
        } else {
          updatedStatus[headerName] = [...currentValues, optionValue];
        }
        return updatedStatus;
      });
    };

    const handleResetFilter = (event, headerName) => {
      setCheckboxStatus((prevStatus) => {
        const newStatus = { ...prevStatus };
        if (headerName in newStatus) {
          delete newStatus[headerName];
        }
        return newStatus;
      });
      setOpenDropdownField(null);
    };

    return (
      <div
        ref={dropdownRef}
        className={`absolute z-50 w-full rounded bg-white p-3 drop-shadow-2xl dark:bg-stone-900 ${
          isOpen ? 'block' : 'hidden'
        }`}
        style={{ top: 'calc(100% + 0.5rem)', minWidth: '10rem', zIndex: 50 }}
        onClick={() => setShouldCloseDropdown(false)}
      >
        <div className="flex w-full flex-col space-y-1">
          {options.map((option, i) => (
            <label key={i} className="flex items-center space-x-2">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={checkboxStatus[headerName]?.includes(option.value) || false}
                onChange={(e) =>
                  handleFilterOptionClick(e, headerName, option.value)
                }
              />
              <span className="w-full text-xs text-gray-500 dark:text-gray-300">
                {option.label}
              </span>
            </label>
          ))}
        </div>
        <div className="justify-left mt-4 flex">
          <button
            className="w-1/2 rounded bg-gray-500 px-2 py-1 font-semibold text-white hover:bg-gray-600"
            onClick={(e) => handleResetFilter(e, headerName)}
          >
            Reset
          </button>
        </div>
      </div>
    );
  };

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        event.target.closest('.navbar') === null
      ) {
        if (!shouldCloseDropdown) {
          event.stopPropagation();
        }
        setOpenDropdownField(null);
      }
      setShouldCloseDropdown(true);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shouldCloseDropdown]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setCheckboxStatus({});
    setIconFilters({
      documentsWaiting: false,
      hasReciprocal: false,
      reciprocalAllowed: false,
      isProvider: false,
      isConsumer: false,
      masterLinkedVendor: false,
      linkedVendor: false,
    });
    setIsFilterActive(false);
  };

  return (
    <div className="relative bg-white dark:bg-stone-800">
      <div className="header flex justify-between bg-gray-400 p-2 dark:bg-stone-800">
        <div className="search-section flex items-center">
          <input
            type="text"
            className="search-input border-2 border-gray-300 bg-white h-7 px-5 pr-10 rounded-md text-sm focus:outline-none dark:bg-stone-700 dark:text-gray-300 dark:border-stone-700"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const lowerSearch = searchTerm.trim().toLowerCase();
                if (lowerSearch === "tetris:start") {
                  setShowTetrisModal(true);
                  setSearchTerm("");
                } else if (['rickroll', 'rick roll', 'never going to give you up', 'never going to give', 'never gonna give you up', 'never gonna give'].includes(lowerSearch)) {
                  setShowRickRoll(true);
                  setSearchTerm("");
                }
              }
            }}
            placeholder="Search..."
          />
          {showTetrisModal && (
            <TetrisModal isOpen={showTetrisModal} onClose={() => setShowTetrisModal(false)} />
          )}
          {showRickRoll && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-stone-900 rounded-lg p-4 w-1/4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-600 dark:text-gray-100">Never Gonna Give You Up</h2>
                  <button onClick={() => setShowRickRoll(false)} className="text-gray-500 hover:text-gray-700">✕</button>
                </div>
                <iframe
                  width="100%"
                  height="315"
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
                  title="Rick Roll"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          )}
          <div className="summary ml-4 flex">
            <div className="group relative">
              <div className="summary-label select-none text-sm font-semibold text-gray-50" style={{ minWidth: '50px' }}>
                Total: {summaryData.total}
              </div>
              <div className="summary-content absolute left-0 z-50 mt-2 hidden w-48 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 group-hover:block dark:bg-stone-600 dark:text-gray-300">
                <div className="select-none py-1">
                  <span className="summary-item block px-4 py-2 text-sm">Active: {summaryData.active}</span>
                  <span className="summary-item block px-4 py-2 text-sm">Inactive: {summaryData.inactive}</span>
                </div>
              </div>
            </div>
            <div className="legend-section relative ml-3 flex items-center">
              <div className="filter-icon-container" style={{ width: '35px' }}>
                {isFilterActive && (
                  <MdFilterAltOff className="mr-2 mt-0.5 cursor-pointer text-gray-300" onClick={clearAllFilters} />
                )}
              </div>
              <div className="legend-trigger flex cursor-pointer items-center" onClick={toggleLegend}>
                <span className="mr-2 text-sm font-semibold text-gray-50">Icons</span>
                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {showLegend && (
                <div className="legend-tooltip absolute left-0 top-full z-50 mt-2 rounded border border-gray-200 bg-white p-2 shadow-lg dark:border-stone-600 dark:bg-stone-700" style={{ minWidth: '14rem', textAlign: 'left' }} ref={legendRef}>
                  {Object.entries(iconConfigurations).map(([key, { icon, label }]) => (
                    <label key={key} className="flex items-center space-x-2 p-1">
                      <input
                        type="checkbox"
                        name="filterGroup"
                        checked={iconFilters[key]}
                        onChange={() => toggleFilter(key)}
                        className="form-radio rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="flex items-center text-sm text-gray-900 dark:text-gray-300">{icon}{label}</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="columns-trigger ml-4 flex cursor-pointer items-center" onClick={(e) => handleDropdownClick(e, 'columns')}>
                <span className="mr-2 text-sm font-semibold text-gray-50">Columns</span>
                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {openDropdownField === 'columns' && <ColumnsDropdown />}
            </div>
          </div>
        </div>
        <div className="flex">
          {hideInActive && (
            <button
              onClick={handleInActive}
              className="new-website-btn mr-2 rounded px-1 font-semibold text-gray-900 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100"
              title="Show Inactive"
            >
              Show Inactive
            </button>
          )}
          <button
            onClick={handleNewVendorClick}
            className="new-website-btn rounded bg-blue-600 px-2 font-semibold text-white hover:bg-blue-500 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            New Vendor
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="export-button ml-2 max-h-8 rounded bg-gray-500 px-4 font-semibold text-white hover:bg-gray-600"
          >
            <img src={exportIcon} alt="Export" className="mb-1 mr-1 inline-block h-4 w-4"/>
            Export
          </button>
          {role === 'Admin' && (
            <button
              onClick={onSwitchToImports}
              className="import-button ml-2 max-h-8 flex items-center gap-2 rounded bg-gray-500 px-4 font-semibold text-white hover:bg-gray-600"
            >
              <LuImport className="mt-1 h-[18px] w-[18px]"/>
              Import
            </button>
          )}
        </div>
      </div>
      <div className="table-container flex flex-col overflow-y-scroll"
           style={{minHeight: '43.75rem', maxHeight: maxHeight}}>
        <table className="min-w-full divide-gray-200">
          <thead className="z-9 sticky top-0 z-40 bg-gray-500 dark:bg-stone-600">
          <tr>
            {visibleColumns.map((column, index) => (
              <th
                key={column.field}
                scope="col"
                className="header-item transform select-none px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-white transition-all duration-200 ease-in-out"
                style={{ width: column.width || 'auto', position: 'relative' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {['Vendor', 'Vendor Code', 'Status', 'feedTypeString', 'vendorTypeString', 'activeSites'].includes(column.field) && (
                      <div className="sort-icons flex items-center">
                        <span onClick={(e) => {
                          if (e.target.classList.contains('sort-icon') || e.target.parentElement.classList.contains('sort-icons') || e.target === e.currentTarget.children[0]) {
                            requestSort(column.field);
                          }
                        }}>
                          <span
                            className={`sort-icon cursor-pointer ${getSortDirection(column.field) === 'ascending' ? "text-white font-bold" : "text-gray-400"}`}
                            style={{ fontSize: '0.6rem', marginLeft: '0.5rem' }}
                          >
                            ▲
                          </span>
                          <span
                            className={`sort-icon cursor-pointer ${getSortDirection(column.field) === 'descending' ? "text-white font-bold" : "text-gray-400"}`}
                            style={{ fontSize: '0.6rem' }}
                          >
                            ▼
                          </span>
                        </span>
                      </div>
                    )}
                    {column.filter && (
                      <div className="filter-icon ml-1" onMouseDown={(e) => handleDropdownClick(e, column.field)}>
                        <FilterIcon className="h-2.5 w-2.5 cursor-pointer" />
                      </div>
                    )}
                  </div>
                  {openDropdownField === column.field && (
                    <FilterDropdown field={column.field} isOpen={openDropdownField !== null} />
                  )}
                </div>
                {index < visibleColumns.length - 1 && (
                  <div
                    className="absolute right-0 top-0 h-full w-[15px] cursor-col-resize"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startWidth = column.width || 160;
                      let latestVisibility = columnVisibility;
                      const onMouseMove = (moveEvent) => {
                        const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
                        latestVisibility = handleResize(column.field, newWidth);
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                        commitResize(latestVisibility);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  >
                    <div className="absolute right-0 top-0 h-full w-[2px] bg-white dark:bg-stone-700" />
                  </div>
                )}
              </th>
            ))}
          </tr>
          </thead>
          <tbody className="table-body divide-y divide-gray-200 bg-white dark:divide-stone-700 dark:bg-stone-800 dark:text-gray-300">
          {isInitialLoad && data.length === 0 ? (
            <tr className="w-full bg-gray-100 dark:bg-stone-900" style={{ height: maxHeight }}>
              <td colSpan={visibleColumns.length}>
                <div className="flex justify-center items-center h-full">
                  <FaSpinner className="animate-spin h-6 w-6 text-blue-600 dark:text-indigo-600 mr-2" />
                  <p className="font-semibold text-blue-600 dark:text-indigo-600">Loading...</p>
                </div>
              </td>
            </tr>
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item, rowKey) => (
              <tr
                key={rowKey}
                onClick={(e) => handleRowClick(item.id, e)}
                className={`${rowKey === 0 ? 'dark:border-stone-700 dark:bg-stone-800' : ''} cursor-pointer hover:bg-gray-100 dark:hover:bg-stone-900 ${item.Status === 'Inactive' ? 'text-gray-400 dark:text-gray-500' : ''}`}
              >
                {visibleColumns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className="cell overflow-hidden text-ellipsis whitespace-nowrap px-6 py-4 text-xs"
                    style={{ width: column.width, maxWidth: column.width }}
                  >
                    {column.field === 'Vendor' ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '0.9375rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.Vendor}
                        </span>
                        {item.hasReciprocal === true && (
                          <img title="Has Reciprocal" src={recip} alt="Recip" className="h-[16px] w-[14px] rounded-sm recip" style={{ marginLeft: '0.9375rem' }} />
                        )}
                        {item.reciprocalAllowed === true && (
                          <img title="Reciprocal Allowed" src={recipAllowed} alt="RecipAllowed" className="h-[16px] w-[14px] rounded-sm recipAllowed" style={{ marginLeft: '0.9375rem' }} />
                        )}
                        {item.isProvider === true && (
                          <img title="Is Provider" src={provider} alt="Provider" className="h-[16px] w-[14px] rounded-sm provider" style={{ marginLeft: '0.9375rem' }} />
                        )}
                        {item.isConsumer === true && (
                          <img title="Is Consumer" src={consumer} alt="Consumer" className="h-[16px] w-[14px] rounded-sm consumer" style={{ marginLeft: '0.9375rem' }} />
                        )}
                        {item.linkedGroup && item.linkedGroup.length >= 2 && item.isPrimary === true && (
                          <span title={'Master Linked Vendor'}><FaLink style={{ color: 'gold', marginLeft: '10px' }} /></span>
                        )}
                        {item.linkedGroup && item.linkedGroup.length >= 2 && item.isPrimary === false && (
                          <span title={'Linked Vendor'}><FaLink style={{ color: 'gray', marginLeft: '10px' }} /></span>
                        )}
                      </div>
                    ) : (
                      item[column.field] || 'N/A'
                    )}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr className="w-full bg-gray-100 dark:bg-stone-900" style={{ height: maxHeight }}>
              <td colSpan={visibleColumns.length}>
                <div className="flex justify-center items-center h-full">
                  <p className="font-semibold text-gray-600 dark:text-gray-400">No Results</p>
                </div>
              </td>
            </tr>
          )}
          </tbody>
        </table>
        <div className="flex-grow"></div>
      </div>
      {isVendorOpen && (
        <VendorModal onClose={closeVendorModal} vendor={selectedVendor} />
      )}
      {showExportModal && (
        <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} title="Export Data">
          <div className="flex flex-col space-y-4 py-2 pl-2 pr-16">
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(exportOptions).map(([key, value], index) => exportKeyLabels[key] && (
                <label key={key} className={`flex items-center space-x-2 ${index >= 9 ? 'col-start-2' : ''}`}>
                  <input
                    type="checkbox"
                    name={key}
                    checked={value}
                    onChange={(e) => setExportOptions({ ...exportOptions, [e.target.name]: e.target.checked })}
                    className="form-checkbox rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    disabled={isExporting}
                  />
                  <span className="text-gray-700 dark:text-gray-300">{exportKeyLabels[key]}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between space-x-4">
              <label className="block">
                <span className="text-gray-700 dark:text-gray-200">From Year:</span>
                <input
                  type="number"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 block w-36 rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                  placeholder="YYYY"
                  min="1900"
                  max={new Date().getFullYear()}
                  style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
                  disabled={isExporting}
                />
              </label>
              <label className="block">
                <span className="text-gray-700 dark:text-gray-200">Until Year:</span>
                <input
                  type="number"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1 block w-36 rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                  placeholder="YYYY"
                  min="1900"
                  max={new Date().getFullYear()}
                  style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
                  disabled={isExporting}
                />
              </label>
            </div>
            <div className="flex justify-end space-x-2 pt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="rounded bg-gray-300 px-4 py-2 font-bold text-black hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 disabled:opacity-50"
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                className="rounded bg-blue-500 px-4 py-2 font-bold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
              >
                {isExporting ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                    <span>Exporting...</span>
                  </div>
                ) : "Export"}
              </button>
            </div>
          </div>
        </ExportModal>
      )}
      {showSuccess && <SuccessNotification show={showSuccess} onClose={closeNotification} text={text} />}
      {showError && <ErrorNotification show={showError} onClose={closeNotification} text={text} />}
    </div>
  );
};

export default VendorTable;